import { useState, useMemo } from 'react';
import { HelpCircle, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export interface FaqItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FaqSectionProps {
  title?: string;
  items: FaqItem[];
}

export function FaqSection({ title = 'FAQ & Help', items }: FaqSectionProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      item =>
        item.question.toLowerCase().includes(q) ||
        (typeof item.answer === 'string' && item.answer.toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <div>
      <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
        <HelpCircle className="w-5 h-5 text-primary" /> {title}
      </h3>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search FAQs..."
          className="pl-9 pr-8 h-9 rounded-xl border text-sm font-body"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground font-body text-center py-4">
          No results for "{search}"
        </p>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {filtered.map(item => (
            <AccordionItem key={item.id} value={item.id}>
              <AccordionTrigger className="text-sm font-body text-left">{item.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground font-body">{item.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
